// react_frontend/src/Components/Modules/UserSetting/ActivityLog.jsx
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { MdOutlineSearch } from "react-icons/md";
import { FaSync } from "react-icons/fa";
import { Input, Loader, Button } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import Swal from "sweetalert2";

// Activity Log Table
const ActivityLogTable = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  // Fetch logs with pagination + search
  const fetchLogs = async (pageNumber = page, rowsPerPage = perPage, searchText = search) => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("/activity-logs", {
        params: { page: pageNumber, per_page: rowsPerPage, search: searchText },
      });

      setLogs(res.data.data || []);
      setTotalRows(res.data.total || 0);
    } catch (error) {
      console.error("Error fetching logs:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to load activity logs",
        text: "Click retry to try again.",
        showCancelButton: true,
        confirmButtonText: "Retry",
      }).then((result) => {
        if (result.isConfirmed) fetchLogs(pageNumber, rowsPerPage, searchText);
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs(page, perPage, search);
  }, [page, perPage, search]);

  // Columns (only 3)
  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{(page - 1) * perPage + index + 1}</strong>,
      width: "80px",
      center: true,
    },
    {
      name: "Description",
      selector: (row) => row.description,
      grow: 6,
      cell: (row) => (
        <span className="text-dark fw-semibold">{row.description}</span>
      ),
    },
    {
      name: "Date & Time",
      selector: (row) => row.created_at,
      sortable: true,
      width: "220px",
      cell: (row) =>
        new Date(row.created_at).toLocaleString("en-GB", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
    },
  ];

  // Custom Styling
  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "13px",
        minHeight: "50px",
      },
    },
    cells: {
      style: {
        padding: "10px 8px",
      },
    },
  };

  return (
    <>
      <Breadcrumb title="Activity Logs" />

      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <i className="fas fa-clipboard-list me-2"></i>
          Activity Logs
        </h5>

        <div className="d-flex gap-2 w-100 w-md-auto">
          <Input
            placeholder="Search by description..."
            value={search}
            onChange={setSearch}
            prefix={<MdOutlineSearch />}
            style={{ width: 300 }}
            size="md"
          />
          <Button
            appearance="ghost"
            startIcon={<FaSync />}
            onClick={() => fetchLogs(page, perPage, search)}
            size="md"
            loading={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={logs}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={(p) => setPage(p)}
        onChangeRowsPerPage={(newPerPage) => {
          setPerPage(newPerPage);
          setPage(1);
        }}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[50, 100, 250]}
        progressPending={loading}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading logs...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <h5>No logs found</h5>
            <p>Try adjusting your search filter.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />
    </>
  );
};

export default ActivityLogTable;